import _ from 'lodash';
import moment from 'moment-timezone';
import { ImsObject } from 'app/shared/collections/ims_object';
import { ZoneHistoryResource } from 'app/shared/resources';
import { ZoneService } from 'app/shared/zones/zone.service';
import { cartographicPolygonCentroid, LatLon } from 'app/shared/utilities/mapping_utils';
import { Cesium } from 'app/cesium';

export const IMS_TYPE__ZONE_HISTORY = 'zone_history';

function openPolygon(polygon) {
    // cesium wants 'open' polygons
    return polygon.slice(0, -1).map(([longitude, latitude]) => {
        return { longitude, latitude };
    });
}

function closePolygon(polygon) {
    // postgis wants 'closed' polygons (same first and last points)
    return _.concat(polygon, [polygon[0]]).map(({ longitude, latitude }) => {
        return [longitude, latitude];
    });
}

function cartesian3CentroidOfPolygon(polygon) {
    const { longitude, latitude } = cartographicPolygonCentroid(polygon);
    return new Cesium.Cartesian3.fromDegrees(longitude, latitude);
}

export class ZoneHistory extends ImsObject {
    _polygon: LatLon[];
    _polygonAsCartesian3s: Cesium.Cartesian3[];
    _polygonCentroidCartesian3: Cesium.Cartesian3;
    _cachedUnixTime: number;

    constructor(resource = new ZoneHistoryResource()) {
        super(IMS_TYPE__ZONE_HISTORY, resource);
    }

    applyResourceChange(resource) {
        super.applyResourceChange(resource);
        this._polygon = resource.polygon ? openPolygon(resource.polygon) : undefined;
        this._updateComputedProperties(this._polygon);
        this._cachedUnixTime = moment(this.timestamp).unix();
    }

    _updateComputedProperties(polygon) {
        this._polygonAsCartesian3s = polygon
            ? ZoneService.polygonToCartesian3s(polygon)
            : undefined;
        this._polygonCentroidCartesian3 = polygon
            ? cartesian3CentroidOfPolygon(polygon)
            : undefined;
    }

    unixTime() {
        return this._cachedUnixTime;
    }

    get timestamp() {
        return this.resource.timestamp;
    }

    set timestamp(timestamp) {
        this._cachedUnixTime = moment(timestamp).unix();
        this.resource.timestamp = timestamp;
    }

    get zoneId(): number {
        return this.resource.zone_id;
    }

    set zoneId(zoneId: number) {
        this.resource.zone_id = zoneId;
    }

    get polygonAsCartesian3s(): Cesium.Cartesian3[] {
        return this._polygonAsCartesian3s;
    }

    get polygonCentroidCartesian3(): Cesium.Cartesian3 {
        return this._polygonCentroidCartesian3;
    }

    get polygon(): LatLon[] {
        return this._polygon;
    }

    set polygon(polygon: LatLon[]) {
        this.resource.polygon = closePolygon(polygon);
        this._polygon = polygon;
        this._updateComputedProperties(this._polygon);
    }

    clone(): ZoneHistory {
        const resourceClone = _.clone(this.resource);
        const result = new ZoneHistory(resourceClone);
        result._polygon = _.clone(this._polygon);
        result._updateComputedProperties(result._polygon);
        return result;
    }
}
